import React from 'react';

// eslint-disable-next-line lumapps/do-not-import-react-query
import * as rq from '@tanstack/react-query';

import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { BaseApiError } from '../types';
import { getStatusFromQuery } from './utils';

export type UseQueryOptions<
    TQueryFnData = any,
    TError = BaseApiError,
    TData = TQueryFnData,
    TQueryKey extends rq.QueryKey = rq.QueryKey,
> = Pick<rq.UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'enabled' | 'cacheTime' | 'staleTime'>;

export type UseQueryResult<TData = unknown, TError = BaseApiError> = rq.UseQueryResult<TData, TError> & {
    /** base loading status for the current query */
    baseLoadingStatus: BaseLoadingStatus;
};

/**
 * React Query Use Query, used when doing API calls to services.
 * Please take a look at the examples on how to implement it.
 *
 * @example
 * import { useQuery } from '@lumapps/base-api/react-query';
 *
 * import { getRunDetail, microAppRunKeys, UseRunDetailParams } from '../../api';
 * import { MicroAppRun } from '../../types';
 *
 * export const useRunDetail = (params: UseRunDetailParams) =>
 *   useQuery<MicroAppRun>({
 *     ...options,
 *     queryKey: microAppRunKeys.detail(params.id, params.extensionId),
 *     queryFn: ({ signal }) => getRunDetail(params.id, params.extensionId, signal),
 *   });
 *
 * @param options
 * @returns UseQueryResult
 */
export function useQuery<
    TQueryFnData = unknown,
    TError = BaseApiError,
    TData = TQueryFnData,
    TQueryKey extends rq.QueryKey = rq.QueryKey,
>(options: rq.UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
    const query = rq.useQuery(options);

    return React.useMemo(() => {
        // eslint-disable-next-line deprecation/deprecation
        const baseLoadingStatus = getStatusFromQuery(query);

        return {
            ...query,
            baseLoadingStatus,
        };
    }, [query]);
}
